import { useEffect, useMemo, useState } from "react";
import { findIndex, findWhere } from "underscore";
import { Button } from "reactstrap";

import {
  Field,
  LayoutAndSections,
  Module,
  Section,
} from "../../../types/TenantInterfaces";
import { newToastEvent } from "../../../utils/ToastUtil";
import SectionsAndLayouts from "./SectionsAndLayouts";
import AppSpinner from "../../../elements/AppSpinner";
import StrUtils from "../../../utils/StrUtils";
import AppDrawer from "../AppDrawer";

interface SectionsAndLayouts {
  open: boolean;
  onClose: () => void;
  layout: LayoutAndSections;
  currentModule: Module;
  onSave: ({}) => void;
  setLayoutState: any;
  createMode?: boolean;
  submitting?: boolean;
}

export type LayoutInfo = Omit<LayoutAndSections, "id" | "Default" | "Sections">;

const SectionsAndLayoutsDrawer = ({
  open,
  onClose,
  layout,
  currentModule,
  setLayoutState,
  submitting,
  onSave,
  createMode,
}: SectionsAndLayouts) => {
  const initialSectionsState =
    JSON.parse(JSON.stringify(layout?.Sections ?? "")) ?? [];
  const [error, setError] = useState<string | null>(null);
  const [layoutInfo, setLayoutInfo] = useState<LayoutInfo>({
    Name: layout?.Name ?? "",
    Description: layout?.Description ?? "",
    CoverImage: layout?.CoverImage ?? "",
    DynamicLayoutAssignment: layout?.DynamicLayoutAssignment
      ? layout?.DynamicLayoutAssignment
      : {
          Field:
            currentModule?.Templates?.RecordDetailView?.DynamicLayoutField ||
            "",
          Value: "",
        },
    ShareOptions: layout?.ShareOptions,
  });

  useEffect(() => {
    if (layout) {
      setLayoutInfo({
        Name: layout.Name ?? "",
        Description: layout?.Description ?? "",
        CoverImage: layout?.CoverImage ?? null,
        DynamicLayoutAssignment: layout?.DynamicLayoutAssignment,
        ShareOptions: layout?.ShareOptions,
      });
    }
  }, [layout]);

  let sections = useMemo(() => {
    if (!layout?.Sections?.length) return [];

    const updatedSections: Section[] = [];

    for (let i = 0; i < layout?.Sections?.length; i++) {
      let section = layout?.Sections[i];

      if (section.Type === "RecordSummarySection") {
        const fields = section.Fields.flatMap((fieldInfo: any) => {
          const fieldSchema = findWhere(currentModule?.Fields, {
            APIName: fieldInfo.Field as string,
          });

          if (!fieldSchema) return [];

          return [
            {
              Field: fieldSchema,
              ColSpan: fieldInfo.ColSpan ? fieldInfo.ColSpan : 1,
            },
          ];
        });

        updatedSections.push({
          ...section,
          // @ts-ignore
          Fields: fields,
        });
      } else if (section.Type === "StageProgress") {
        updatedSections.push(section);
      } else if (section.Type === "RichTextSection") {
        updatedSections.push(section);
      }
    }

    return updatedSections ?? [];
  }, [layout, createMode]);

  const addStageProgressWidget = () => {
    let id = StrUtils.uuidv4();

    sections.push({
      id,
      Type: "StageProgress",
      Title: "Progress Widget",
      Description: "",
      ProgressField: "",
      Stages: Array<any>(),
      CompleteStage: false,
    });

    setLayoutState({
      sections,
      [`addField${id}`]: "",
    });
  };

  const addRichTextWidget = () => {
    const id = StrUtils.uuidv4();

    sections.push({
      id,
      Type: "RichTextSection",
      Title: "Rich Text Widget",
      Field: undefined,
      Description: "",
    });

    setLayoutState({
      sections,
    });
  };

  const handleOnClose = () => {
    setLayoutState({ layout: { ...layout, Sections: initialSectionsState } });
    setLayoutInfo({
      Name: "",
      Description: "",
      CoverImage: null,
      DynamicLayoutAssignment: undefined,
      ShareOptions: undefined,
    });
    setError("");
    onClose();
  };

  // Function to define "out" in sumbit() function depending on section type
  const sectionType = (section: Section) => {
    if (section.Type === "RecordSummarySection" || !section.Type) {
      return {
        id: section.id,
        Fields: Array(),
        Title: section.Title,
        Description: section.Description,
        Type: section.Type,
      };
    }

    if (section.Type === "StageProgress") {
      return {
        id: section.id,
        Type: section.Type,
        Title: section.Title,
        Description: section.Description,
        ProgressField: section.ProgressField,
        Stages: Array(),
        CompleteStage: section.CompleteStage,
      };
    }

    if (section.Type === "RichTextSection") {
      return {
        id: section.id,
        Type: section.Type,
        Title: section.Title,
        Description: section.Description,
        Field: section.Field,
      };
    }
  };

  // Function that creates variable ProgressField{sect.id} in this.state and adds the ProgressField to the StageProgress section
  const handleProgressFieldChange = (event: any, sect: any) => {
    let idx = findIndex(sections, { id: sect.id });
    const { name, value } = event.target;
    setLayoutState({
      [name]: value,
    });

    const selectedStage = sections[idx];

    if (selectedStage.Type !== "StageProgress") return;

    selectedStage.Stages = [];
    selectedStage.ProgressField = value;
  };

  const submit = () => {
    let filteredSections: any[] = [];
    for (let i = 0; i < sections.length; i++) {
      let sect = sections[i];
      let out = sectionType(sect);

      if (!sect.Title) {
        setError("Please include a name on all sections to continue");
        return;
      }
      if (!sect.Type || !out) {
        setError("The section type doesn't exist");
        return;
      }
      if (sect.Type === "RecordSummarySection") {
        for (let j = 0; j < sect.Fields.length; j++) {
          const field = sect.Fields[j];

          out?.Fields?.push({
            ColSpan: field.ColSpan ? field.ColSpan : 1,
            Field: (field.Field as Field).APIName,
          });
        }
      }
      if (sect.Type === "RichTextSection") {
        out.Field = sect.Field;
      }
      if (sect.Type === "StageProgress") {
        for (let j = 0; j < sect.Stages.length; j++) {
          const stage = sect.Stages[j];
          out?.Stages?.push({
            ColSpan: 1,
            Stage: stage.Stage,
          });
        }
        if (!sect.ProgressField) {
          setError(
            "Please include a tracked progress field on all progress widgets to continue"
          );
          return;
        }
      }

      filteredSections.push(out);
    }

    if (
      Array.isArray(layoutInfo?.ShareOptions) &&
      !layoutInfo?.ShareOptions?.length
    ) {
      layoutInfo.ShareOptions = null;
    }

    let updatedLayout = {
      action: !!layout ? "update" : "create",
      layout: {
        id: layout?.id ?? StrUtils.uuidv4(),
        Default: layout?.Default ?? false,
        ...layoutInfo,
        Sections: filteredSections,
      },
    };

    onSave({
      moduleAPIName: currentModule.APIName,
      layoutSections: updatedLayout,
      dynamicLayoutField: layoutInfo?.DynamicLayoutAssignment?.Field,
      onError: setError,
      onSuccess: () => {
        setLayoutState((prev: any) => ({
          ...prev,
          layouts:
            updatedLayout.action === "update"
              ? prev.layouts.map((l: any) => {
                  if (l.id === updatedLayout.layout.id) {
                    return updatedLayout.layout;
                  }

                  return l;
                })
              : prev.layouts.push(updatedLayout.layout),
        }));
        handleOnClose();
        newToastEvent("success", "Successfully saved record detail layout");
      },
    });
  };

  return (
    <AppDrawer
      placement="right"
      full
      backdrop="static"
      open={open}
      onClose={handleOnClose}
      headerTitle={createMode ? "Creating Layout" : "Edit Layout"}
      headerOptions={
        <Button
          color="primary"
          size="sm"
          style={{ width: 100 }}
          onClick={submit}
          disabled={submitting}
        >
          {submitting ? <AppSpinner color="white" /> : "Save"}
        </Button>
      }
      bodyCSS="pt-0 pb-8"
      drawerStyle={{
        width: "100%",
        transition: "width 0.05s ease-in",
      }}
      body={
        <SectionsAndLayouts
          error={error}
          layoutInfo={layoutInfo}
          currentModule={currentModule}
          sections={sections}
          setLayout={setLayoutState}
          handleProgressFieldChange={handleProgressFieldChange}
          addStageProgressWidget={addStageProgressWidget}
          addRichTextWidget={addRichTextWidget}
          setLayoutInfo={setLayoutInfo}
        />
      }
    />
  );
};

export default SectionsAndLayoutsDrawer;
